import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useRef } from 'react';

import { VoiceAIRunTimeline } from '@/components/voice-agent/VoiceAIRunTimeline';
import { useGetEnhancedBlandCallWorkflowsQuery } from '@/services/api/blandService';
import { VoiceAIAgentRunSchema } from '@/types/enhanced-bland-call';

type BPOHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  workflowId: string;
};

const Divider: React.FC = () => <div className="h-px bg-gray-100" />;

export const BPOHistoryModal: React.FC<BPOHistoryModalProps> = ({
  isOpen,
  onClose,
  matterId,
  workflowId,
}) => {
  const { data, isFetching } = useGetEnhancedBlandCallWorkflowsQuery(
    { matterId },
    { skip: !isOpen || !matterId },
  );

  const run: VoiceAIAgentRunSchema | undefined = useMemo(() => {
    const workflows: unknown = data;
    const list: VoiceAIAgentRunSchema[] =
      (workflows as { workflows?: VoiceAIAgentRunSchema[] })?.workflows || [];
    return list.find((w) => w.workflow_id === workflowId);
  }, [data, workflowId]);

  const dialogRef = useRef<HTMLDivElement | null>(null);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bpo-history-title"
    >
      <div
        className="absolute inset-0 bg-black/40"
        role="button"
        tabIndex={0}
        aria-label="Close history modal"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />
      <div
        ref={dialogRef}
        className="relative z-10 w-[700px] max-w-[95vw] max-h-[85vh] overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/10 flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <div>
            <h2
              id="bpo-history-title"
              className="text-base font-semibold text-gray-900"
            >
              Run History
            </h2>
            <p className="text-xs text-gray-500 font-mono">{workflowId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close history modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {isFetching ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              Loading run details…
            </div>
          ) : run ? (
            <VoiceAIRunTimeline run={run} showCollectedData />
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-500">
              Run not found
            </div>
          )}
        </div>

        <Divider />
        <div className="flex items-center justify-end gap-2 px-4 py-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BPOHistoryModal;
